import { HelpPathOutlineIcon } from '@ikyucom/design-system-icons-react'
import { InputText, SelectBox } from 'macaron-frontend-ui'
import React, { useCallback } from 'react'
import Image from '~/components/image/Image'
import {
  CC_AMEX_IMAGE_URL,
  CC_DINERS_IMAGE_URL,
  CC_JCB_IMAGE_URL,
  CC_MASTER_IMAGE_URL,
  CC_VISA_IMAGE_URL,
} from '~/lib/constants/constants'
import { css, sva } from '~/styled-system/css'
import { useSelectedCardStatus } from '../store/card'
import {
  useCardExpirationError,
  useCardExpirationOptions,
  useCardHolderError,
  useCardNumberError,
  useSecurityCodeError,
  useSetCardExpireMonth,
  useSetCardExpireYear,
  useSetCardHolder,
  useSetCardNumber,
  useSetSecurityCode,
} from './card-payment'

const newCardInputStyles = sva({
  slots: ['container', 'brands', 'icon', 'bottom'],
  base: {
    container: {
      spaceY: { base: '3', _pc: '4' },
    },
    brands: {
      marginTop: { base: '3', _pc: '5' },
      display: 'flex',
      alignItems: 'center',
      columnGap: '2',
    },
    icon: {
      height: '[24px]',
      width: 'auto',
    },
    bottom: {
      _sd: {
        spaceY: '4',
      },
      _pc: {
        display: 'grid',
        gridTemplateColumns: '2',
        columnGap: '6',
      },
    },
  },
})

/** 事前カード決済用クレジットカードの新規登録フォーム */
// 決済代行会社は GMO を利用している
export function NewCardInput() {
  const selected = useSelectedCardStatus()
  if (selected?.type !== 'new') {
    return null
  }
  const classes = newCardInputStyles()
  return (
    <div className={classes.container}>
      <div className={classes.brands}>
        <Image src={CC_VISA_IMAGE_URL} width={24} height={24} className={classes.icon} alt="Visa" />
        <Image
          src={CC_MASTER_IMAGE_URL}
          width={24}
          height={24}
          className={classes.icon}
          alt="MasterCard"
        />
        <Image src={CC_JCB_IMAGE_URL} width={24} height={24} className={classes.icon} alt="JCB" />
        <Image
          src={CC_AMEX_IMAGE_URL}
          width={24}
          height={24}
          className={classes.icon}
          alt="AmericanExpress"
        />
        <Image
          src={CC_DINERS_IMAGE_URL}
          width={24}
          height={24}
          className={classes.icon}
          alt="DinersClub"
        />
      </div>
      <CardNumberInput />
      <CardHolderInput />
      <div className={classes.bottom}>
        <CardExpirationInput />
        <SecurityCodeInput />
      </div>
    </div>
  )
}

function CardNumberInput() {
  const error = useCardNumberError()
  const set = useSetCardNumber()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      set(e.target.value)
    },
    [set]
  )
  return (
    <div className={css({ spaceY: '2' })}>
      <p className={css({ fontWeight: 'bold' })}>カード番号</p>
      <InputText
        onChange={onChange}
        width={300}
        placeholder="0000 1111 2222 3333"
        withSpread={true}
        errorMessage={error}
        inputMode="numeric"
      />
    </div>
  )
}

function CardHolderInput() {
  const error = useCardHolderError()
  const set = useSetCardHolder()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      set(e.target.value)
    },
    [set]
  )
  return (
    <div className={css({ spaceY: '2' })}>
      <p className={css({ fontWeight: 'bold' })}>カード名義</p>
      <InputText
        onChange={onChange}
        width={300}
        placeholder="TARO IKYU"
        withSpread={true}
        errorMessage={error}
      />
    </div>
  )
}

function CardExpirationInput() {
  const { error, options, onChangeMonth, onChangeYear } = useCardExpirationInput()
  return (
    <div className={css({ spaceY: '2' })}>
      <p className={css({ fontWeight: 'bold' })}>有効期限（月/年）</p>
      <div className={css({ display: 'flex', alignItems: 'center', columnGap: '2' })}>
        <SelectBox onChange={onChangeMonth} label="" width={100} values={options.month} />
        <div className={css({ fontSize: 'md' })}>月</div>
        <div className={css({ fontSize: 'md' })}>/</div>
        <SelectBox onChange={onChangeYear} label="" width={100} values={options.year} />
        <div className={css({ fontSize: 'md' })}>年</div>
      </div>
      {error !== undefined && (
        <p className={css({ mt: '1', fontSize: 'xs', color: 'error.text' })}>{error}</p>
      )}
    </div>
  )
}

function useCardExpirationInput() {
  const error = useCardExpirationError()
  const options = useCardExpirationOptions()
  const setMonth = useSetCardExpireMonth()
  const setYear = useSetCardExpireYear()
  const onChangeMonth = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = Number(e.target.value) < 10 ? `0${e.target.value}` : e.target.value
      setMonth(value)
    },
    [setMonth]
  )
  const onChangeYear = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setYear(e.target.value)
    },
    [setYear]
  )
  return { error, options, onChangeMonth, onChangeYear }
}

function useSecurityCodeInput() {
  const error = useSecurityCodeError()
  const set = useSetSecurityCode()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      set(e.target.value)
    },
    [set]
  )
  return { error, onChange }
}

const securityCodeInputStyles = sva({
  slots: ['container', 'heading'],
  base: {
    container: {
      spaceY: '2',
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '1',
    },
  },
})

function SecurityCodeInput() {
  const { error, onChange } = useSecurityCodeInput()
  const classes = securityCodeInputStyles()
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <p className={css({ fontWeight: 'bold' })}>セキュリティコード</p>
        <a
          href="https://faq.ikyu.com/app/answers/detail/a_id/1519/c/46"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="セキュリティコードとは"
        >
          <HelpPathOutlineIcon className={css({ color: 'gray.700' })} />
        </a>
      </div>
      <InputText
        onChange={onChange}
        width={100}
        placeholder="123"
        errorMessage={error}
        inputMode="numeric"
      />
    </div>
  )
}
